import { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { DailyTotal } from '../../types/analytics';
import { pivotDailyTotals } from '../../utils/analyticsTransformers';

interface DailyTrendsChartProps {
  dailyTotals: DailyTotal[];
  title: string;
  metrics: { key: string; label: string; color: string }[];
  syncId?: string;
}

export default function DailyTrendsChart({ dailyTotals, title, metrics, syncId }: DailyTrendsChartProps) {
  const data = useMemo(() => pivotDailyTotals(dailyTotals), [dailyTotals]);

  if (data.length === 0) {
    return (
      <Card className="bg-admin-dark-800 border-admin-slate-300/10">
        <CardHeader><CardTitle className="text-heading-5 text-admin-slate-400">{title}</CardTitle></CardHeader>
        <CardContent><p className="text-admin-slate-500 text-sm">No data available for this period.</p></CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-admin-dark-800 border-admin-slate-300/10">
      <CardHeader>
        <CardTitle className="text-heading-5 text-admin-slate-400">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} syncId={syncId} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis
              dataKey="date"
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              tickFormatter={(v: string) => v.slice(5)}
            />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
              labelStyle={{ color: '#cbd5e1' }}
              itemStyle={{ color: '#94a3b8' }}
            />
            <Legend wrapperStyle={{ color: '#94a3b8' }} />
            {metrics.map((m) => (
              <Line
                key={m.key}
                type="monotone"
                dataKey={m.key}
                name={m.label}
                stroke={m.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
